import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloudServerOutlined,
  DatabaseOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  FileSearchOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
  StopOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Collapse,
  Empty,
  Grid,
  Progress,
  Skeleton,
  Steps,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import type { ColumnsType, TableProps } from "antd/es/table";
import type { UploadFile, UploadProps } from "antd/es/upload/interface";
import moment from "moment";

import ReusableUpload from "../upload/ReusableUpload";
import styles from "./ImportExperience.module.scss";

const { Panel } = Collapse;
const { Step } = Steps;
const { Text, Title } = Typography;

export interface ImportExperienceRecord {
  id: string;
  originalFilename: string;
  status: string;
  startedAt?: string;
  finishedAt?: string;
  totalLines: number;
  processedLines: number;
  correct: number;
  incorrect: number;
  percentCompleted: number;
}

interface ImportExperienceProps<T extends ImportExperienceRecord> {
  title: React.ReactNode;
  instructions: React.ReactNode;
  accept: string;
  uploading: boolean;
  fileList: UploadFile[];
  onUploadChange: UploadProps["onChange"];
  onUploadRequest?: (options: {
    file: File;
    onSuccess: (result?: unknown, file?: File) => void;
    onError: (error: unknown) => void;
  }) => void | Promise<void>;
  autoStart: boolean;
  onAutoStartChange: (checked: boolean) => void;
  records: T[];
  loading: boolean;
  columns: ColumnsType<T>;
  pagination?: TableProps<T>["pagination"];
  onRefresh: () => void;
  renderFeaturedActions: (record: T) => React.ReactNode;
  renderMobileActions?: (record: T) => React.ReactNode;
  onDelete?: (record: T) => void;
}

const phaseSteps = [
  {
    label: "Import-PhaseQueued",
    icon: <ClockCircleOutlined />,
  },
  {
    label: "Import-PhaseStarting",
    icon: <CloudServerOutlined />,
  },
  {
    label: "Import-PhaseReading",
    icon: <FileSearchOutlined />,
  },
  {
    label: "Import-PhaseValidating",
    icon: <CheckCircleOutlined />,
  },
  {
    label: "Import-PhaseApplying",
    icon: <DatabaseOutlined />,
  },
  {
    label: "Import-PhaseFinalizing",
    icon: <SyncOutlined />,
  },
];

const numberFormatter = new Intl.NumberFormat("es-UY");

const clampPercent = (value?: number) =>
  Math.min(100, Math.max(0, Number.isFinite(value) ? Number(value) : 0));

const phaseStepIndex = (record: ImportExperienceRecord) => {
  if (record.status === "PENDING") return 0;
  if (record.status === "COMPLETED") return phaseSteps.length;

  const percent = clampPercent(record.percentCompleted);
  if (percent < 2) return 1;
  if (percent < 15) return 2;
  if (percent < 55) return 3;
  if (percent < 95) return 4;
  return 5;
};

const formatDuration = (durationMs: number) => {
  if (!Number.isFinite(durationMs) || durationMs < 0) return "—";
  const totalSeconds = Math.max(0, Math.floor(durationMs / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) return `${hours} h ${minutes} min`;
  if (minutes > 0) return `${minutes} min ${seconds} s`;
  return `${seconds} s`;
};

export const elapsedFor = (record: ImportExperienceRecord, clock: number) => {
  if (!record.startedAt) return "—";
  const startedAt = Date.parse(record.startedAt);
  const finishedAt = record.finishedAt
    ? Date.parse(record.finishedAt)
    : record.status === "PROCESSING"
    ? clock
    : Number.NaN;
  return formatDuration(finishedAt - startedAt);
};

const estimatedRemainingFor = (
  record: ImportExperienceRecord,
  clock: number
) => {
  if (record.status !== "PROCESSING" || !record.startedAt) return "—";
  const percent = clampPercent(record.percentCompleted);
  if (percent < 3 || percent >= 100) return null;

  const elapsedMs = clock - Date.parse(record.startedAt);
  return formatDuration((elapsedMs / percent) * (100 - percent));
};

function ImportExperience<T extends ImportExperienceRecord>({
  title,
  instructions,
  accept,
  uploading,
  fileList,
  onUploadChange,
  onUploadRequest,
  autoStart,
  onAutoStartChange,
  records,
  loading,
  columns,
  pagination,
  onRefresh,
  renderFeaturedActions,
  renderMobileActions,
  onDelete,
}: ImportExperienceProps<T>) {
  const { t } = useTranslation();
  const screens = Grid.useBreakpoint();
  const isMobileList = !screens.md;
  const [clock, setClock] = useState(Date.now());

  const activeImport = useMemo(
    () => records.find((record) => record.status === "PROCESSING"),
    [records]
  );
  const featuredImport = activeImport || records[0];

  useEffect(() => {
    if (!activeImport) return;
    const interval = window.setInterval(() => setClock(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, [activeImport]);

  const phaseLabel = (record: ImportExperienceRecord) => {
    if (record.status === "COMPLETED") return t("Import-PhaseCompleted");
    if (record.status === "CANCELLED") return t("Import-PhaseCancelled");
    if (record.status === "ERROR") return t("Import-PhaseFailed");
    return t(
      phaseSteps[phaseStepIndex(record)]?.label || "Import-PhaseStarting"
    );
  };

  const statusTag = (status: string) => {
    switch (status) {
      case "PENDING":
        return <Tag icon={<ClockCircleOutlined />}>{t("Common-Pending")}</Tag>;
      case "PROCESSING":
        return (
          <Tag color="processing" icon={<SyncOutlined spin />}>
            {t("Common-Processing")}
          </Tag>
        );
      case "COMPLETED":
        return (
          <Tag color="success" icon={<CheckCircleOutlined />}>
            {t("Common-Completed")}
          </Tag>
        );
      case "ERROR":
        return (
          <Tag color="error" icon={<ExclamationCircleOutlined />}>
            {t("Common-Error")}
          </Tag>
        );
      case "CANCELLED":
        return (
          <Tag color="orange" icon={<StopOutlined />}>
            {t("Common-Cancelled")}
          </Tag>
        );
      default:
        return <Tag>{status}</Tag>;
    }
  };

  const renderFeaturedCard = (record: T) => {
    const percent = clampPercent(record.percentCompleted);
    const remaining = estimatedRemainingFor(record, clock);
    const isProcessing = record.status === "PROCESSING";
    const isTerminal = ["COMPLETED", "ERROR", "CANCELLED"].includes(
      record.status
    );

    return (
      <Card className={styles.liveCard} bordered={false} aria-live="polite">
        <div className={styles.liveHeader}>
          <div className={styles.liveIdentity}>
            <Text className={styles.eyebrow}>
              {isProcessing
                ? t("Import-ActiveImport")
                : t("Import-LatestImport")}
            </Text>
            <div className={styles.liveTitleRow}>
              <Tooltip title={record.originalFilename}>
                <Title level={4} ellipsis>
                  {record.originalFilename}
                </Title>
              </Tooltip>
              {statusTag(record.status)}
            </div>
            <Text type="secondary">{phaseLabel(record)}</Text>
          </div>
          <div className={styles.liveActions}>
            {renderFeaturedActions(record)}
          </div>
        </div>

        <div className={styles.progressHeader}>
          <Text strong>{phaseLabel(record)}</Text>
          <Text strong>{percent.toFixed(1)}%</Text>
        </div>
        <Progress
          percent={percent}
          showInfo={false}
          strokeWidth={10}
          status={
            record.status === "ERROR"
              ? "exception"
              : record.status === "COMPLETED"
              ? "success"
              : record.status === "CANCELLED"
              ? "normal"
              : "active"
          }
        />

        <div className={styles.phaseRail}>
          <Steps
            size="small"
            current={phaseStepIndex(record)}
            status={record.status === "ERROR" ? "error" : "process"}
            responsive
          >
            {phaseSteps.map((step) => (
              <Step key={step.label} title={t(step.label)} icon={step.icon} />
            ))}
          </Steps>
        </div>

        <div className={styles.metricsGrid}>
          <div className={styles.metric}>
            <Text type="secondary">{t("Import-RowsRead")}</Text>
            <strong>
              {numberFormatter.format(record.processedLines || 0)}
              <span>
                {" / "}
                {record.totalLines
                  ? numberFormatter.format(record.totalLines)
                  : "—"}
              </span>
            </strong>
          </div>
          <div className={styles.metric}>
            <Text type="secondary">{t("Common-Errors")}</Text>
            <strong className={record.incorrect > 0 ? styles.errorValue : ""}>
              {numberFormatter.format(record.incorrect || 0)}
            </strong>
          </div>
          <div className={styles.metric}>
            <Text type="secondary">{t("Import-Elapsed")}</Text>
            <strong>{elapsedFor(record, clock)}</strong>
          </div>
          <div className={styles.metric}>
            <Text type="secondary">{t("Import-Remaining")}</Text>
            <strong>
              {remaining === null ? t("Import-Calculating") : remaining}
            </strong>
          </div>
        </div>

        {isProcessing && (
          <Alert
            className={styles.backgroundAlert}
            type="info"
            showIcon
            message={t("Import-BackgroundTitle")}
            description={t("Import-BackgroundDescription")}
          />
        )}

        {isTerminal && (
          <Alert
            className={styles.backgroundAlert}
            type={
              record.status === "COMPLETED"
                ? "success"
                : record.status === "ERROR"
                ? "error"
                : "warning"
            }
            showIcon
            message={
              record.status === "COMPLETED"
                ? t("Import-CompletedSummary", {
                    correct: numberFormatter.format(record.correct || 0),
                    errors: numberFormatter.format(record.incorrect || 0),
                  })
                : record.status === "ERROR"
                ? t("Import-FailedSummary")
                : t("Import-CancelledSummary")
            }
          />
        )}
      </Card>
    );
  };

  return (
    <div className={styles.container}>
      <Title level={2}>{title}</Title>

      <Card className={styles.uploadCard} size="small">
        <Collapse ghost>
          <Panel
            key="instructions"
            header={
              <span>
                <InfoCircleOutlined /> {t("Common-ImportInstructions")}
              </span>
            }
          >
            <div className={styles.instructions}>{instructions}</div>
          </Panel>
        </Collapse>
        <div className={styles.uploadSection}>
          <div className={styles.fileSelection}>
            <ReusableUpload
              accept={accept}
              disabled={uploading}
              loading={uploading}
              fileList={fileList}
              onChange={onUploadChange}
              onUploadRequest={onUploadRequest}
              mode="compact"
              fullWidth={isMobileList}
              showUploadButton
            />
          </div>
          <Checkbox
            checked={autoStart}
            onChange={(event) => onAutoStartChange(event.target.checked)}
          >
            {t("Common-AutoStart")}
          </Checkbox>
        </div>
      </Card>

      {loading && records.length === 0 ? (
        <Card className={styles.liveCard} bordered={false}>
          <Skeleton active paragraph={{ rows: 4 }} />
        </Card>
      ) : (
        featuredImport && renderFeaturedCard(featuredImport)
      )}

      <Card className={styles.historyCard} size="small">
        <div className={styles.cardHeader}>
          <Title level={4}>{t("Common-ImportHistory")}</Title>
          <Button
            icon={<ReloadOutlined />}
            onClick={onRefresh}
            loading={loading}
            size="small"
          >
            {t("Common-Refresh")}
          </Button>
        </div>

        {loading && records.length === 0 ? (
          <Skeleton active paragraph={{ rows: 6 }} />
        ) : isMobileList ? (
          records.length > 0 ? (
            <div className={styles.mobileHistoryList}>
              {records.map((record) => {
                const percent = clampPercent(record.percentCompleted);

                return (
                  <Card
                    key={record.id}
                    size="small"
                    className={styles.mobileHistoryCard}
                  >
                    <div className={styles.mobileRecordHeader}>
                      <div className={styles.mobileRecordIdentity}>
                        <Tooltip title={record.originalFilename}>
                          <Text strong ellipsis>
                            {record.originalFilename}
                          </Text>
                        </Tooltip>
                        <Text type="secondary">{phaseLabel(record)}</Text>
                      </div>
                      {statusTag(record.status)}
                    </div>
                    <div className={styles.mobileProgressHeader}>
                      <Text type="secondary">{t("Common-Progress")}</Text>
                      <Text strong>{percent.toFixed(1)}%</Text>
                    </div>
                    <Progress
                      percent={percent}
                      size="small"
                      showInfo={false}
                      status={
                        record.status === "ERROR"
                          ? "exception"
                          : record.status === "COMPLETED"
                          ? "success"
                          : undefined
                      }
                    />
                    <div className={styles.mobileRecordMetrics}>
                      <div>
                        <Text type="secondary">{t("Import-RowsRead")}</Text>
                        <strong>
                          {numberFormatter.format(record.processedLines || 0)} /{" "}
                          {numberFormatter.format(record.totalLines || 0)}
                        </strong>
                      </div>
                      <div>
                        <Text type="secondary">{t("Common-Errors")}</Text>
                        <strong
                          className={
                            record.incorrect > 0 ? styles.errorValue : ""
                          }
                        >
                          {numberFormatter.format(record.incorrect || 0)}
                        </strong>
                      </div>
                    </div>
                    <div className={styles.mobileRecordActions}>
                      {(renderMobileActions || renderFeaturedActions)(record)}
                      {onDelete && record.status !== "PROCESSING" && (
                        <Button
                          danger
                          size="small"
                          icon={<DeleteOutlined />}
                          onClick={() => onDelete(record)}
                        >
                          {t("Common-Delete")}
                        </Button>
                      )}
                    </div>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )
        ) : (
          <Table<T>
            columns={columns}
            dataSource={records}
            rowKey="id"
            size="small"
            rowClassName={(record) =>
              record.status === "PROCESSING" ? styles.processingRow : ""
            }
            expandable={{
              expandedRowRender: (record) => (
                <div className={styles.expandedDetails}>
                  <div>
                    <Text type="secondary">{t("Import-CurrentPhase")}</Text>
                    <strong>{phaseLabel(record)}</strong>
                  </div>
                  <div>
                    <Text type="secondary">{t("Import-RowsRead")}</Text>
                    <strong>
                      {numberFormatter.format(record.processedLines || 0)} /{" "}
                      {numberFormatter.format(record.totalLines || 0)}
                    </strong>
                  </div>
                  <div>
                    <Text type="secondary">{t("Import-Elapsed")}</Text>
                    <strong>{elapsedFor(record, clock)}</strong>
                  </div>
                  <div>
                    <Text type="secondary">{t("Common-Started")}</Text>
                    <strong>
                      {record.startedAt
                        ? moment(record.startedAt).format("DD/MM/YYYY HH:mm:ss")
                        : "—"}
                    </strong>
                  </div>
                </div>
              ),
            }}
            pagination={pagination}
          />
        )}
      </Card>
    </div>
  );
}

export default ImportExperience;
